import { ChevronRightIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ProfilePicture } from "@/components/Utilities/ProfilePicture";
import type { CommunityWithStats } from "@/hooks/useCommunities";
import { PAGES } from "@/utilities/pages";

interface CommunityCardProps {
  community: CommunityWithStats;
}

export const CommunityCard = ({ community }: CommunityCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [_cardWidth, setCardWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (cardRef.current) {
        setCardWidth(cardRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (cardRef.current) {
      resizeObserver.observe(cardRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Extract data from the API response structure
  const name = community.details?.name || community.uid;
  const slug = community.details?.slug;
  const imageURL = community.details?.logoUrl;
  const stats = {
    grants: community.stats?.totalGrants || 0,
    projects: community.stats?.totalProjects || 0,
    members: community.stats?.totalMembers || 0,
  };

  return (
    <div
      ref={cardRef}
      className="flex flex-col p-4 border border-gray-200 dark:border-zinc-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 ease-in-out w-full min-w-0"
      style={{ height: "318px" }}
    >
      <div className="flex justify-center mb-3 min-h-[72px] h-18 mx-auto">
        <ProfilePicture
          imageURL={imageURL}
          name={name}
          size="72"
          className="w-full h-full max-w-[72px] max-h-[72px] flex-shrink-0 object-cover"
          alt={`${name} logo`}
        />
      </div>

      <div className="text-center mb-3 min-w-0">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white line-clamp-2 break-words">
          {name}
        </h3>
      </div>

      <div className="flex justify-center space-x-4 mb-3 min-w-0">
        <div className="text-center min-w-0">
          <div className="text-base font-bold text-neutral-600 dark:text-neutral-400">
            {stats.projects.toLocaleString()}
          </div>
          <div className="text-base font-normal text-neutral-600 dark:text-neutral-400 truncate">
            Projects
          </div>
        </div>
        <div className="text-center min-w-0">
          <div className="text-base font-bold text-neutral-600 dark:text-neutral-400">
            {stats.grants.toLocaleString()}
          </div>
          <div className="text-base font-normal text-neutral-600 dark:text-neutral-400 truncate">
            Grants
          </div>
        </div>
        <div className="text-center min-w-0">
          <div className="text-base font-bold text-neutral-600 dark:text-neutral-400">
            {stats.members.toLocaleString()}
          </div>
          <div className="text-base font-normal text-neutral-600 dark:text-neutral-400 truncate">
            Members
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-auto pt-2">
        <Link
          href={PAGES.COMMUNITY.ALL_GRANTS(slug || community.uid)}
          className="flex items-center justify-center w-full lg:w-20 h-10 text-sm font-semibold rounded flex-shrink-0"
        >
          <span>Go</span>
          <ChevronRightIcon className="h-4 w-4 ml-1" />
        </Link>
      </div>
    </div>
  );
};
